import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { routinesService, Task } from '../../services/supabase/routines';

export function useRoutines(profileId?: string) {
    const queryClient = useQueryClient();

    const { data: predefinedRoutines = [], isLoading: templatesLoading } = useQuery({
        queryKey: ['predefined_routines'],
        queryFn: routinesService.getPredefinedRoutines
    });

    const { data: profileRoutines = [], isLoading: routinesLoading } = useQuery({
        queryKey: ['routines', profileId],
        queryFn: () => profileId ? routinesService.getRoutinesForProfile(profileId) : Promise.resolve([]),
        enabled: !!profileId
    });

    const createRoutineMutation = useMutation({
        mutationFn: routinesService.createRoutine,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['routines'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['day_plan'] });
        }
    });

    const updateRoutineMutation = useMutation({
        mutationFn: ({ routineId, updates }: { routineId: string; updates: Parameters<typeof routinesService.updateRoutine>[1] }) => 
            routinesService.updateRoutine(routineId, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['routines'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['day_plan'] });
        }
    });

    const deleteRoutineMutation = useMutation({
        mutationFn: routinesService.deleteRoutine,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['routines'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['day_plan'] });
        }
    });

    return {
        predefinedRoutines,
        profileRoutines,
        templatesLoading,
        routinesLoading,
        createRoutine: createRoutineMutation.mutateAsync,
        updateRoutine: updateRoutineMutation.mutateAsync,
        isUpdatingRoutine: updateRoutineMutation.isPending,
        deleteRoutine: deleteRoutineMutation.mutateAsync,
        isDeletingRoutine: deleteRoutineMutation.isPending
    };
}

// Hook specifically for getting tasks for a specific day
export function useDayPlan(profileId?: string, date?: string) {
    const { data: tasks = [], isLoading, refetch } = useQuery<Task[]>({
        queryKey: ['day_plan', profileId, date],
        queryFn: () => {
            if (!profileId || !date) return Promise.resolve([]);
            return routinesService.getDayPlan(profileId, date);
        },
        enabled: !!profileId && !!date
    });

    return { tasks, isLoading, refetch };
}

// Hook for fetching routines by IDs (fallback when profile lookup fails)
export function useRoutinesByIds(routineIds: string[], enabled: boolean = true) {
    const { data: routines = [], isLoading } = useQuery({
        queryKey: ['routines_by_id', routineIds],
        queryFn: () => routinesService.getRoutinesByIds(routineIds),
        enabled: enabled && routineIds.length > 0
    });

    return { routines, isLoading };
}
